import * as THREE from 'three';
import { isSketchGroup, type SketchGroup } from '../../../mesh/sketch';
import { BossExtrudeFeatureBindingFactory } from './BossExtrudeFeatureBindingFactory';
import type { ExtrudeFeaturePrototype } from '../../prototype/extrude/ExtrudeFeaturePrototype';
import type { ExtrudeFeatureDefinitionModel } from '../../definition/extrude/ExtrudeFeatureDefinition';
import { BossExtrudeSceneResolver } from './BossExtrudeSceneResolver';
import { CutExtrudeDefinitionCoordinator } from './CutExtrudeDefinitionCoordinator';
import { ExtrudeFeatureDefinitionBuilder } from './ExtrudeFeatureDefinitionBuilder';
import type { ExtrudeFeatureEditDefinition } from './ExtrudeFeatureEditDefinition';
import { ExtrudeFeatureEditDefinitionSanitizer } from './ExtrudeFeatureEditDefinitionSanitizer';
import {
    ExtrudeHostedTargetBodyResolver,
    type ExtrudeHostedTargetResolution,
} from './ExtrudeHostedTargetBodyResolver';
import { ExtrudeFaceSelectionTargetResolver } from './ExtrudeFaceSelectionTargetResolver';
import { ExtrudeEndTargetResolver } from './ExtrudeEndTargetResolver';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/extrude 层；
 * 2. 负责承载拉伸族共享的编辑桥接主链；
 * 3. 该基类只处理共享桥接能力，具体语义由 boss / cut 子类通过构造参数固化。
 *
 * 文件职责：
 * 1. 从 session selection 解析 target 与 binding；
 * 2. 从 prototype 恢复编辑定义并构建 preview / commit 载荷；
 * 3. 为拉伸族子类提供统一扩展点，避免把 cut 逻辑继续堆在 boss 命名文件中。
 */
export abstract class ExtrudeFeatureSessionBridge {
    /** 拉伸场景轮廓解析器。 */
    private readonly sceneResolver = new BossExtrudeSceneResolver();
    /** 拉伸面目标解析器。 */
    private readonly faceSelectionTargetResolver = new ExtrudeFaceSelectionTargetResolver();
    /** 宿主目标体解析器。 */
    private readonly hostedTargetBodyResolver = new ExtrudeHostedTargetBodyResolver();
    /** 当前桥接器绑定的特征种类。 */
    private readonly featureKind: ExtrudeFeatureKind;
    /** 当前桥接器绑定的操作类型。 */
    private readonly operation: ExtrudeOperation;

    /**
     * @param input 构造参数。
     */
    protected constructor(input: {
        featureKind: ExtrudeFeatureKind;
        operation: ExtrudeOperation;
    }) {
        this.featureKind = input.featureKind;
        this.operation = input.operation;
    }

    /**
     * 从 prototype 恢复编辑定义。
     *
     * @param prototype 拉伸 prototype。
     * @returns 编辑定义。
     */
    public restoreDefinitionFromPrototype(
        prototype: ExtrudeFeaturePrototype,
    ): ExtrudeFeatureEditDefinition {
        const projection = prototype.getDefinition().toProjection();
        const restoredDefinition: ExtrudeFeatureEditDefinition = {
            featureKind: projection.featureKind,
            operation: projection.operation,
            direction1: {
                ...projection.direction1,
                draft: {
                    ...projection.direction1.draft,
                },
            },
            direction2: {
                ...projection.direction2,
                draft: {
                    ...projection.direction2.draft,
                },
            },
            thinFeature: {
                ...projection.thinFeature,
            },
            mergeResult: projection.mergeResult,
            selectedContoursInputActive: false,
            cutOptions: projection.cutOptions,
        };

        return ExtrudeFeatureEditDefinitionSanitizer.sanitize(restoredDefinition);
    }

    /**
     * 构建拉伸特征的预览输入方法。
     *
     * 该方法的作用是：
     * - 接收外部输入参数（包括featureId、用户当前拉伸选择、编辑参数定义、场景对象）。
     * - 基于当前选择(selection)和场景(scene)，恢复出拉伸操作所需的目标对象（如选中的轮廓/面/草图等）。
     * - 如果无法恢复目标，则抛出错误，提示当前选择不合法。
     * - 构建绑定信息（bindings），用于描述特征和三维场景之间的关联映射。
     * - 结合恢复的目标对象、用户输入的编辑参数(definition)、绑定信息、场景环境等，组装出完整的拉伸特征定义（definition）。
     * - 最终返回完整的 ExtrudeExecutionInput 结构，用于拉伸预览计算和渲染。
     *
     * 该方法主要用于在拉伸编辑过程中，用户参数变动或对象选择变动后，实时构建后台预览拉伸所需的输入载荷，
     * 从而驱动后端/内核重新计算拉伸预览效果（与SolidWorks“拉伸特征动态交互预览”完全对齐，严禁随意更改）。
     *
     * @param input 输入参数对象：
     *   - featureId?: string | null，当前被编辑的特征ID（新建时为null）
     *   - selection: ExtrudeSessionSelection，用户在界面上选中的拉伸草图或面的描述
     *   - definition: ExtrudeFeatureEditDefinition，当前面板编辑参数定义
     *   - scene: THREE.Scene | null，当前场景对象（用于查找选中对象的实际引用）
     * @returns ExtrudeExecutionInput 预览执行载荷，供后台拉伸预览渲染与验证
     * @throws 当当前拉伸选择草稿无法恢复为特征输入时。
     */
    public buildPreviewInput(input: {
        featureId?: string | null;
        selection: ExtrudeSessionSelection;
        definition: ExtrudeFeatureEditDefinition;
        scene: THREE.Scene | null;
    }): ExtrudeExecutionInput {
        // 基于场景与选择草稿恢复拉伸目标（必须严格还原SolidWorks逻辑）
        const target = this.resolveTargetFromSelection(input.scene, input.selection);
        if (!target) {
            // 选区无效时抛出异常，保障类型安全与交互一致性
            throw new Error('当前拉伸选择草稿无法恢复为特征输入');
        }

        // 从目标对象与场景生成特征与三维元素的序列化绑定关系
        const bindings = this.buildSerializedBindings(target, input.scene);

        // 组装最终的拉伸特征定义（严格遵循SolidWorks拉伸预览全量特征结构）
        const builtDefinition = this.buildFeatureDefinition(target, input.definition, bindings, input.scene);
        return {
            featureKind: this.featureKind,
            featureId: input.featureId,
            definition: builtDefinition,
            bindings: builtDefinition.getCommandParams().referenceBindings ?? bindings,
        };
    }

    /**
     * 构建提交输入。
     *
     * @param input 输入参数。
     * @returns 提交执行载荷。
     */
    public buildCommitInput(input: {
        featureId?: string | null;
        selection: ExtrudeSessionSelection;
        definition: ExtrudeFeatureEditDefinition;
        scene: THREE.Scene | null;
    }): ExtrudeExecutionInput {
        return this.buildPreviewInput(input);
    }

    /**
     * 作用：
     * 本函数用于根据当前 THREE.Scene 场景和用户草图/面的选择（ExtrudeSessionSelection），恢复 SolidWorks 拉伸操作中的“拉伸目标”对象（ExtrudeSessionTarget）。
     * —— 其逻辑：完全对齐 SolidWorks，在草图区域选择和三维面选择之间区分数据恢复路径，
     *   1. 若为“contours”草图区域选择，优先直接用 selection.contours 快照（snapshot）恢复目标，确保拉伸预览/提交完全一致、无信息损失。
     *   2. 快照无效时，降级通过 contourId 在场景复原几何，并用区域输入整体生成 ExtrudeSessionTarget。
     *   3. 若为面选择（面特征拉伸），则委托 faceSelectionTargetResolver。  
     * 若一切都无法解析拉伸目标，则返回 null，以符合 SolidWorks 交互中无效选区直接阻断流程的机制。
     *
     * @param scene 当前 THREE.js 场景对象。用于解析选区实际对象引用。
     * @param selection ExtrudeSessionSelection，用户界面传递的“拉伸选区”。
     * @returns ExtrudeSessionTarget|null 成功则为有效拉伸目标，否则为 null（表示当前选区不能生成有效特征）。 
     */
    public resolveTargetFromSelection(
        scene: THREE.Scene | null,
        selection: ExtrudeSessionSelection,
    ): ExtrudeSessionTarget | null {
        // 1. 草图轮廓选区处理：优先用contours快照兜底，保证SolidWorks一致性
        if (selection.kind === 'contours') {
            // 1.1 优先使用快照数据恢复，确保轮廓拓扑、类型及坐标准确还原
            const snapshotTarget = this.resolveTargetFromContourSnapshots(selection.contours);
            if (snapshotTarget) {
                return snapshotTarget;
            }

            // 1.2 快照失效时，通过 contourIds 在场景中恢复全部有效轮廓
            const contours = selection.contourIds.flatMap((contourId) => {
                const resolved = this.sceneResolver.resolveContoursFromSelection(scene, contourId);
                // 解析失败的跳过
                if (!Array.isArray(resolved)) {
                    return [];
                }
                // resolved 为有效轮廓数组
                return resolved;
            });
            if (contours.length === 0) {
                // 没有任何有效轮廓，直接失败
                return null;
            }
            // 组装为区域输入格式，再生成拉伸目标
            return this.buildTargetFromRegionInputs(contours.map((contour) => ({
                regionId: contour.id,
                geometry: contour.geometry,
                sketchId: contour.sketchId,
                contourSourceObjectId: contour.sourceObjectId,
                contourKey: contour.contourKey,
                regionIdentity: contour.regionIdentity,
            })));
        }

        // 2. 面选择处理：交由专用解析器统一恢复目标，对应SolidWorks“拉伸-面”逻辑
        return this.faceSelectionTargetResolver.resolve({
            scene,
            objectId: selection.objectId,
            faceIndex: selection.faceIndex,
            sourceType: selection.sourceType,
        })?.target ?? null;
    }

    /**
     * 优先从 contour 快照恢复拉伸目标。
     *
     * @param contours 纯数据轮廓快照。
     * @returns 拉伸目标；快照不可用时返回 null。
     */
    private resolveTargetFromContourSnapshots(
        contours: ExtrudeSessionRegionInput[] | undefined,
    ): ExtrudeSessionTarget | null {
        if (!Array.isArray(contours) || contours.length <= 0) {
            return null;
        }
        return this.buildTargetFromRegionInputs(contours);
    }

    /**
     * 从纯数据区域输入构建拉伸目标。
     *
     * 设计说明：
     * 1. 前端选择阶段已经拿到了完整 `ProfileRegionGeometry`，其中包含 `outerSegments/holeSegments`；
     * 2. 预览和提交应优先消费这份快照，避免只传 ID 后二次解析时把曲线轮廓降级为点列；
     * 3. 场景对象解析仍作为兜底，用于兼容旧调用方。
     *
     * @param regions 区域输入。
     * @returns 拉伸目标；区域无效时返回 null。
     */
    private buildTargetFromRegionInputs(
        regions: ExtrudeSessionRegionInput[],
    ): ExtrudeSessionTarget | null {
        const validRegions = regions.filter((region) => (
            typeof region.sketchId === 'string'
            && region.sketchId.length > 0
            && region.geometry
            && Array.isArray(region.geometry.outer)
            && region.geometry.outer.length >= 3
        ));
        if (validRegions.length <= 0) {
            return null;
        }

        const sketchId = validRegions[0]!.sketchId;
        const sameSketchRegions = validRegions.filter((region) => region.sketchId === sketchId);
        if (sameSketchRegions.length <= 0) {
            return null;
        }

        const regionGeometries = sameSketchRegions.map((region) => structuredClone(region.geometry));
        const contourKeys = sameSketchRegions.map((region) => region.contourKey ?? region.regionId);
        const center = this.computeRegionInputsCenter(sameSketchRegions);
        const normal = new THREE.Vector3(...regionGeometries[0]!.normal);

        return {
            params: {
                sourceType: 'region_geometry',
                sketchId,
                contourKeys,
                regionGeometry: regionGeometries[0],
                regionGeometries,
            },
            normal: this.normalizeVector(normal),
            basePoint: center,
            regions: sameSketchRegions.map((region, index) => ({
                regionId: region.regionId,
                geometry: regionGeometries[index]!,
                sketchId: region.sketchId,
                contourSourceObjectId: region.contourSourceObjectId,
                contourKey: region.contourKey,
                regionIdentity: region.regionIdentity
                    ? structuredClone(region.regionIdentity)
                    : undefined,
            })),
        };
    }

    /**
     * 计算区域输入的整体中心。
     *
     * @param regions 区域输入。
     * @returns 区域中心。
     */
    private computeRegionInputsCenter(regions: ExtrudeSessionRegionInput[]): THREE.Vector3 {
        const allOuterPoints = regions.flatMap((region) => region.geometry.outer);
        if (allOuterPoints.length <= 0) {
            return new THREE.Vector3();
        }
        return allOuterPoints.reduce(
            (accumulator, point) => accumulator.add(new THREE.Vector3(point[0], point[1], point[2])),
            new THREE.Vector3(),
        ).multiplyScalar(1 / allOuterPoints.length);
    }

    /**
     * 归一化目标法向。
     *
     * @param normal 原始法向。
     * @returns 有效单位法向。
     */
    private normalizeVector(normal: THREE.Vector3): THREE.Vector3 {
        if (normal.lengthSq() <= 1e-12) {
            return new THREE.Vector3(0, 0, 1);
        }
        return normal.normalize();
    }

    /**
     * 构建拉伸特征绑定信息，并序列化为标准 binding 集合。
     *
     * 作用说明：
     * 该函数根据拉伸目标目标（ExtrudeSessionTarget）和当前场景，收集本次拉伸涉及的草图组（SketchGroup）及其所有轮廓区域信息，
     * 构建序列化的 binding 数据，最终用于特征系统持久化引用关系、模型重建、参数关联（如下次编辑时还能正确寻址到指定草图轮廓、同步重建拓扑等）。
     * 
     * 为什么需要创建 binding?
     * SolidWorks 等参数化建模系统，所有特征（如拉伸/切除）都要记录与其输入轮廓、实体的绑定关系；
     * 以便未来模型重算、参数变更、草图变更时能追溯、自动修正；避免“悬空引用”或“丢失关联”。
     * PuppyCAD严格对齐此特性，每次拉伸操作都必须序列化所有草图轮廓到区域的唯一标识、几何信息与引用上下文，
     * 用于支持全局特征重建、编辑撤销重做等完整流程。
     *
     * binding的用途：
     * - 持久化记录特征-草图轮廓的关联（如操纵/删除时能反向查找）。
     * - 支持模型重构流程，保障参数/引用有效性一致。
     * - 处理撤销/重做、草图变动时保证几何引用不丢失。
     * 
     * @param target ExtrudeSessionTarget 当前拉伸操作目标（含所有输入区域等）
     * @param scene THREE.Scene | null 当前3D场景，辅助查找运行时草图组对象
     * @returns SerializedFeatureReferenceBinding[] 标准化序列化的绑定信息集合
     */
    public buildSerializedBindings(
        target: ExtrudeSessionTarget,
        scene: THREE.Scene | null,
    ): SerializedFeatureReferenceBinding[] {
        // 没有任何区域输入时，无需建立绑定
        if (!target.regions || target.regions.length === 0) {
            return [];
        }

        // 尝试查找并获取对应草图组对象（SketchGroup），用于补充上下文引用
        const runtimeSketchGroup = target.regions[0]?.sketchId
            ? (scene?.getObjectByProperty('uuid', target.regions[0].sketchId) ?? null)
            : null;

        // 校验类型，实际约束只支持 SketchGroup
        const sketchGroup = runtimeSketchGroup && isSketchGroup(runtimeSketchGroup)
            ? runtimeSketchGroup as SketchGroup
            : null;

        // 构建序列化 binding: 每个轮廓区域都记录唯一标识、几何体、引用源等核心信息
        return BossExtrudeFeatureBindingFactory.buildSerializedBindings({
            sketchId: target.regions[0]?.sketchId ?? null,
            sketchGroup,
            contours: target.regions.map((region) => ({
                contourSourceObjectId: region.contourSourceObjectId,
                contourKey: region.contourKey,
                regionGeometry: region.geometry,
                regionIdentity: region.regionIdentity,
            })),
        });
    }

    /**
     * 基于 session target 与编辑定义构建特征定义。
     *
     * @param target 拉伸目标。
     * @param definition 编辑定义。
     * @param bindings 已序列化 binding。
     * @param scene 当前场景。
     * @returns 拉伸特征定义对象。
     */
    public buildFeatureDefinition(
        target: ExtrudeSessionTarget,
        definition: ExtrudeFeatureEditDefinition,
        bindings: SerializedFeatureReferenceBinding[],
        scene: THREE.Scene | null,
    ): ExtrudeFeatureDefinitionModel {
        const hostedTarget = this.resolveHostedTarget(scene, target, definition);
        const nextDefinition = this.buildNextDefinition(target, definition, hostedTarget);
        const endTargetResolver = new ExtrudeEndTargetResolver();
        for (const key of ['direction1', 'direction2'] as const) {
            const direction = nextDefinition[key];
            if (direction.enabled && direction.target && !['blind', 'mid_plane', 'through_all'].includes(direction.endCondition)) {
                direction.target = endTargetResolver.resolveRuntime(direction.target, scene);
            }
        }
        const runtimeSketchSnapshot = this.resolveRuntimeSketchSnapshotFromBindings(bindings);

        return ExtrudeFeatureDefinitionBuilder.buildFeatureDefinition({
            featureKind: this.featureKind,
            baseParams: {
                ...target.params,
                ...runtimeSketchSnapshot,
                featureKind: this.featureKind,
                operation: this.operation,
                referenceBindings: bindings.length > 0 ? bindings : undefined,
            },
            definition: nextDefinition,
        });
    }

    /**
     * 解析面宿主草图对应的完整宿主描述。
     *
     * 该描述只负责草图平面追溯和预览深度提示；最终切除目标仍由 Feature Scope
     * 与刀具体相交结果决定，不能因草图绘制在哪个实体面上而收窄候选池。
     *
     * @param scene 当前场景。
     * @param target 当前拉伸目标。
     * @param definition 当前编辑定义。
     * @returns 完整宿主描述；凸台拉伸返回 null。
     */
    private resolveHostedTarget(
        scene: THREE.Scene | null,
        target: ExtrudeSessionTarget,
        definition: ExtrudeFeatureEditDefinition,
    ): ExtrudeHostedTargetResolution | null {
        return this.featureKind === 'cut-extrude'
            ? this.hostedTargetBodyResolver.resolve({
                scene,
                target,
                definition,
            })
            : null;
    }

    /**
     * 从草图轮廓绑定中提取运行时草图快照。
     *
     * 设计说明：
     * 1. `regionGeometry` 是本次建模的直接输入，但旧数据或中间链路可能缺失 `outerSegments`；
     * 2. 把 runtime 草图快照同步写入 command params 后，Worker 可以在建模前统一恢复真实曲线段；
     * 3. 这里只做纯数据透传，不在桥接层解释具体草图几何语义。
     *
     * @param bindings 已序列化的特征绑定。
     * @returns 可写入拉伸命令参数的运行时草图快照。
     */
    private resolveRuntimeSketchSnapshotFromBindings(
        bindings: SerializedFeatureReferenceBinding[],
    ): Pick<ExtrudeParams, 'runtimeSketchPlane' | 'runtimeSketchEntities'> {
        const sketchProfileBinding = bindings.find((binding) => binding.kind === 'sketch-profile');
        if (!sketchProfileBinding || sketchProfileBinding.kind !== 'sketch-profile') {
            return {};
        }
        return {
            runtimeSketchPlane: sketchProfileBinding.runtimeSketchPlane
                ? structuredClone(sketchProfileBinding.runtimeSketchPlane)
                : undefined,
            runtimeSketchEntities: Array.isArray(sketchProfileBinding.runtimeSketchEntities)
                ? sketchProfileBinding.runtimeSketchEntities.map((entity) => structuredClone(entity))
                : undefined,
        };
    }

    /**
     * 构建写回 definition 的下一份编辑定义。
     *
     * @param target 当前拉伸目标。
     * @param definition 当前编辑定义。
     * @param hostedTarget 完整宿主描述。
     * @returns 下一份编辑定义。
     */
    private buildNextDefinition(
        target: ExtrudeSessionTarget,
        definition: ExtrudeFeatureEditDefinition,
        hostedTarget: ExtrudeHostedTargetResolution | null,
    ): ExtrudeFeatureEditDefinition {
        if (this.featureKind !== 'cut-extrude') {
            return ExtrudeFeatureEditDefinitionSanitizer.sanitize({
                ...definition,
                featureKind: this.featureKind,
                operation: this.operation,
            });
        }

        const synchronizedDefinition = CutExtrudeDefinitionCoordinator.synchronizeDefinition({
            ...definition,
            featureKind: this.featureKind,
            operation: this.operation,
        });
        const baseCutOptions = synchronizedDefinition.cutOptions
            ?? CutExtrudeDefinitionCoordinator.createDefaultCutOptions({
                direction1: synchronizedDefinition.direction1,
                direction2: synchronizedDefinition.direction2,
            });
        const existingHostedContour = baseCutOptions.hostedContourInput;
        const hostedTargetBodyId = hostedTarget?.targetBodyId
            ?? existingHostedContour?.targetBodyId
            ?? null;
        const hostedContourInput = hostedTargetBodyId || existingHostedContour
            ? {
                sketchId: existingHostedContour?.sketchId
                    ?? target.regions?.[0]?.sketchId
                    ?? '',
                contourId: existingHostedContour?.contourId
                    ?? target.regions?.[0]?.regionId
                    ?? '',
                targetBodyId: hostedTargetBodyId,
                supportFaceObjectId: hostedTarget?.supportFaceObjectId
                    ?? existingHostedContour?.supportFaceObjectId
                    ?? null,
                supportFaceFaceIndex: hostedTarget?.supportFaceFaceIndex
                    ?? existingHostedContour?.supportFaceFaceIndex
                    ?? null,
            }
            : undefined;
        const nextDefinition: ExtrudeFeatureEditDefinition = {
            ...synchronizedDefinition,
            cutOptions: {
                ...baseCutOptions,
                hostedContourInput,
                featureScope: baseCutOptions.featureScope ?? {
                    // 对齐 SolidWorks 默认：Selected bodies + Auto-select，由刀具体相交决定目标体。
                    mode: 'selected_bodies',
                    autoSelect: true,
                    initialAutoTargetIds: [],
                },
            },
        };

        return CutExtrudeDefinitionCoordinator.synchronizeDefinition(nextDefinition);
    }
}

/**
 * 拉伸会话区域输入。
 */
export interface ExtrudeSessionRegionInput {
    /** 区域 ID。 */
    regionId: string;
    /** 区域几何。 */
    geometry: import('../../../../types').ProfileRegionGeometry;
    /** 草图 ID。 */
    sketchId: string;
    /** 轮廓源对象 ID。 */
    contourSourceObjectId?: string;
    /** 轮廓键。 */
    contourKey?: string;
    /** 相交闭合区域身份。 */
    regionIdentity?: PersistedRegionIdentity;
}

/**
 * 拉伸会话目标。
 */
export interface ExtrudeSessionTarget {
    /** 基础命令参数。 */
    params: Omit<ExtrudeParams, 'depth' | 'direction'>;
    /** 法向。 */
    normal: THREE.Vector3;
    /** 基点。 */
    basePoint: THREE.Vector3;
    /** 来源 mesh ID。 */
    sourceMeshId?: string;
    /** 已选轮廓。 */
    regions?: ExtrudeSessionRegionInput[];
}

/**
 * 拉伸会话高层选择草稿。
 */
export type ExtrudeSessionSelection =
    | {
        /** 选择类型。 */
        kind: 'contours';
        /** 轮廓对象 ID 列表。 */
        contourIds: string[];
        /** 轮廓纯数据快照；存在时优先用于预览和提交。 */
        contours?: ExtrudeSessionRegionInput[];
    }
    | {
        /** 选择类型。 */
        kind: 'face';
        /** 场景对象 ID。 */
        objectId: string;
        /** 面索引。 */
        faceIndex: number;
        /** 来源类型。 */
        sourceType: 'face' | 'solid_face';
        /** 法向提示。 */
        normalHint?: [number, number, number];
    };

/**
 * 拉伸执行入口载荷。
 */
export interface ExtrudeExecutionInput {
    /** 特征种类。 */
    featureKind: ExtrudeFeatureKind;
    /** 编辑态特征 ID。 */
    featureId?: string | null;
    /** 特征定义。 */
    definition: ExtrudeFeatureDefinitionModel;
    /** 序列化绑定。 */
    bindings: SerializedFeatureReferenceBinding[];
}
